import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key: string): string => {
    return translations[language]?.[key] || translations['en'][key] || key;
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage: (lang: string) => {
      setLanguage(lang);
    },
    t,
  };

  // Translations
  const translations: Record<string, Record<string, string>> = {
    en: {
      'header.title': 'Adaalt Bot',
      'header.subtitle': 'Revolutionizing Legal Solutions with AI',
      'generator.prompt': 'Enter your legal query...',
      'generator.generate': 'Generate',
      'result.copy': 'Copy',
      'result.download': 'Download',
      'result.print': 'Print',
      'result.save': 'Save',
      'result.edit': 'Edit',
      'result.saveEdits': 'Save Edits',
      'result.title': 'Generated Document',
      'tabs.newDocument': 'New Document',
      'tabs.savedDocuments': 'Saved Documents',
      'tabs.scanner': 'Scanner',
      'tabs.collaborate': 'Collaborate',
      'tabs.chatbot': 'Chatbot',
      'message.copied': 'Copied to clipboard!',
      'message.saved': 'Saved successfully!',
      'message.error': 'An error occurred.',
      'saved.noDocuments': 'No saved documents.',
      'saved.selectToDelete': 'Select documents to delete:',
      'saved.deleteSelected': 'Delete Selected',
      'footer.copyright': '© 2024 Adaalt Bot. All rights reserved.',
      'languageSwitcher.english': 'English',
      'languageSwitcher.urdu': 'Urdu',
      'chatbot.inputPlaceholder': 'Ask me anything about law...',
      'chatbot.send': 'Send',
      'scanner.upload': 'Upload Image',
      'scanner.scan': 'Scan Document',
      'scanner.scanning': 'Scanning...',
      'scanner.noText': 'No text found in the image.',
      'scanner.error': 'Error scanning document.',
      'advocate.dashboard': 'Advocate Dashboard',
      'advocate.cases': 'Cases',
      'advocate.templates': 'Templates',
      'advocate.clients': 'Clients',
      'advocate.settings': 'Settings',
      'advocate.logout': 'Logout',
      'sharing.title': 'Share Document',
      'sharing.description': 'Share this document with an advocate or family member.',
      'sharing.documentTitle': 'Document',
      'sharing.recipientEmail': 'Recipient Email',
      'sharing.permission': 'Permission',
      'sharing.viewOnly': 'View only',
      'sharing.canEdit': 'Can edit',
      'sharing.message': 'Message',
      'sharing.messagePlaceholder': 'Add a message...',
      'sharing.cancel': 'Cancel',
      'sharing.shareButton': 'Share Document',
      'sharing.success': 'Document shared successfully',
      'sharing.invalidEmail': 'Please enter a valid email address',
      'sharing.revoke': 'Revoke',
      'sharing.revoked': 'Access revoked successfully',
      'sharing.documents': 'Shared Documents',
      'sharing.sharedByMe': 'Shared By Me',
      'sharing.sharedWithMe': 'Shared With Me',
      'sharing.noDocumentsShared': 'You haven\'t shared any documents yet.',
      'sharing.noDocumentsSharedWithYou': 'No documents have been shared with you yet.',
      'sharing.sharedWith': 'Shared with',
      'sharing.sharedBy': 'Shared by',
      'sharing.open': 'Open',
      'sharing.opening': 'Opening document...',
      'tabs.shared': 'Shared'
    },
    ur: {
      'header.title': 'عدالت بوٹ',
      'header.subtitle': 'اے آئی کے ساتھ قانونی حل میں انقلاب',
      'generator.prompt': 'اپنی قانونی سوال درج کریں...',
      'generator.generate': 'تخلیق کریں',
      'result.copy': 'کاپی',
      'result.download': 'ڈاؤن لوڈ',
      'result.print': 'پرنٹ',
      'result.save': 'محفوظ کریں',
      'result.edit': 'ترمیم کریں',
      'result.saveEdits': 'ترمیم محفوظ کریں',
      'result.title': 'تیار کردہ دستاویز',
      'tabs.newDocument': 'نیا دستاویز',
      'tabs.savedDocuments': 'محفوظ دستاویزات',
      'tabs.scanner': 'سکینر',
      'tabs.collaborate': 'تعاون',
      'tabs.chatbot': 'چیٹ بوٹ',
      'message.copied': 'کلپ بورڈ پر کاپی ہو گیا!',
      'message.saved': 'کامیابی سے محفوظ ہو گیا!',
      'message.error': 'ایک خرابی پیش آئی۔',
      'saved.noDocuments': 'کوئی محفوظ دستاویزات نہیں ہیں۔',
      'saved.selectToDelete': 'حذف کرنے کے لئے دستاویزات منتخب کریں:',
      'saved.deleteSelected': 'منتخب کو حذف کریں',
      'footer.copyright': '© 2024 عدالت بوٹ۔ جملہ حقوق محفوظ ہیں۔',
      'languageSwitcher.english': 'انگریزی',
      'languageSwitcher.urdu': 'اردو',
      'chatbot.inputPlaceholder': 'مجھ سے قانون کے بارے میں کچھ بھی پوچھیں...',
      'chatbot.send': 'بھیجیں',
      'scanner.upload': 'تصویر اپ لوڈ کریں',
      'scanner.scan': 'دستاویز سکین کریں',
      'scanner.scanning': 'سکیننگ...',
      'scanner.noText': 'تصویر میں کوئی متن نہیں ملا۔',
      'scanner.error': 'دستاویز سکین کرنے میں خرابی۔',
      'advocate.dashboard': 'ایڈووکیٹ ڈیش بورڈ',
      'advocate.cases': 'مقدمات',
      'advocate.templates': 'سانچے',
      'advocate.clients': 'موکل',
      'advocate.settings': 'ترتیبات',
      'advocate.logout': 'لاگ آؤٹ',
      'sharing.title': 'دستاویز کا اشتراک کریں',
      'sharing.description': 'اس دستاویز کو کسی وکیل یا خاندان کے رکن کے ساتھ شئیر کریں۔',
      'sharing.documentTitle': 'دستاویز',
      'sharing.recipientEmail': 'وصول کنندہ کا ای میل',
      'sharing.permission': 'اجازت',
      'sharing.viewOnly': 'صرف دیکھیں',
      'sharing.canEdit': 'ترمیم کر سکتے ہیں',
      'sharing.message': 'پیغام',
      'sharing.messagePlaceholder': 'پیغام شامل کریں...',
      'sharing.cancel': 'منسوخ کریں',
      'sharing.shareButton': 'دستاویز شئیر کریں',
      'sharing.success': 'دستاویز کامیابی سے شئیر کی گئی',
      'sharing.invalidEmail': 'برائے مہربانی درست ای میل درج کریں',
      'sharing.revoke': 'رسائی واپس لیں',
      'sharing.revoked': 'رسائی کامیابی سے واپس لے لی گئی',
      'sharing.documents': 'اشتراک شدہ دستاویزات',
      'sharing.sharedByMe': 'میرے ذریعے شئیر کی گئیں',
      'sharing.sharedWithMe': 'میرے ساتھ شئیر کی گئیں',
      'sharing.noDocumentsShared': 'آپ نے ابھی تک کوئی دستاویز شئیر نہیں کی ہے۔',
      'sharing.noDocumentsSharedWithYou': 'ابھی تک آپ کے ساتھ کوئی دستاویز شئیر نہیں کی گئی ہے۔',
      'sharing.sharedWith': 'شئیر کی گئی',
      'sharing.sharedBy': 'شئیر کی گئی از',
      'sharing.open': 'کھولیں',
      'sharing.opening': 'دستاویز کھول رہے ہیں...',
      'tabs.shared': 'اشتراک شدہ'
    },
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
